import { z } from 'zod';
import { paginationQuerySchema, paginated } from './common.js';
import { auditEventSchema } from './audit.js';
import { normalisedEmailSchema } from './tenant.js';
import { invitationIssuedSchema } from './invitation.js';
/**
 * Platform-scoped contracts. Routes using these are protected by
 * `@PlatformAuthorized()` and carry NO tenant context — they must never accept
 * a tenantId in a body (see `.strict()` below and ADR 0008).
 */
// ── platform audit read ──────────────────────────────────────────────────────
/**
 * Query for the platform audit trail. `scope` selects which slice of
 * AuditEvent is returned:
 *   - 'platform' (default): only rows with tenantId IS NULL — the platform
 *     actions that the tenant-scoped audit API structurally cannot return;
 *   - 'all': platform rows plus tenant rows, for cross-tenant investigation;
 *   - 'tenant': rows for one explicitly named tenant.
 *
 * `tenantId` here is a *filter on a platform-admin-only route*, not a tenant
 * context assertion — it never establishes authorization. Ordinary users can
 * never reach this route at all.
 */
export const platformAuditListQuerySchema = paginationQuerySchema
    .extend({
    sort: z.enum(['at']).default('at'),
    scope: z.enum(['platform', 'tenant', 'all']).default('platform'),
    tenantId: z.string().uuid().optional(),
    entityType: z.string().optional(),
    entityId: z.string().uuid().optional(),
    action: z.string().optional(),
    actorId: z.string().uuid().optional(),
    dateFrom: z.coerce.date().optional(),
    dateTo: z.coerce.date().optional(),
})
    .strict();
/** Platform audit rows expose tenantId, which the tenant-scoped DTO omits. */
export const platformAuditEventSchema = auditEventSchema.extend({
    tenantId: z.string().uuid().nullable(),
});
export const platformAuditListResponseSchema = paginated(platformAuditEventSchema);
// ── platform user management ─────────────────────────────────────────────────
/**
 * Create a global identity.
 *
 * There is NO password field: passwords are never accepted from a client. The
 * server generates a temporary one and returns it exactly once (ADR 0009 §2 and
 * the tenant-provisioning precedent).
 *
 * There is also NO `isPlatformAdmin` field. Granting the platform capability is
 * a distinct, higher-privilege operation and is deliberately out of scope here,
 * so this endpoint can never mint a platform administrator.
 */
export const createPlatformUserRequestSchema = z
    .object({
    email: normalisedEmailSchema,
    displayName: z.string().trim().min(1).max(120),
    /**
     * Send a single-use invitation instead of minting a temporary password.
     *
     * ⚠️ **Safe UNCONDITIONALLY on this path, which is not true of the others.** An
     * invitation may only ever be issued for an identity the call just minted — issuing one
     * against a REUSED identity would be a password-set link for an address the caller does
     * not own, which is account takeover wearing an onboarding flow's clothes. The company
     * and partner paths therefore pass `created` through as a guard, because they REUSE an
     * existing account by design (`User` is global, ADR 0009).
     *
     * This endpoint refuses a duplicate outright with `UserEmailConflictError` → 409, so it
     * never reuses anything and `created` is always true here. The guard is passed anyway
     * rather than hard-coded, so the day this endpoint learns to reuse, it is one argument
     * rather than a silent hole.
     *
     * Defaulted FALSE, so nothing about an existing caller changes (D13).
     */
    invite: z.boolean().default(false),
})
    .strict();
/**
 * Update a global identity. `status` transitions are what disable an account
 * platform-wide; setting INACTIVE increments tokenVersion, so existing sessions
 * die on their next request.
 */
export const updatePlatformUserRequestSchema = z
    .object({
    displayName: z.string().trim().min(1).max(120).optional(),
    status: z.enum(['ACTIVE', 'INACTIVE']).optional(),
})
    .strict()
    .refine((v) => Object.keys(v).length > 0, { message: 'At least one field must be provided' });
export const platformUserListQuerySchema = paginationQuerySchema
    .extend({
    sort: z.enum(['displayName', 'email', 'createdAt']).default('displayName'),
    direction: z.enum(['asc', 'desc']).default('asc'),
    search: z.string().trim().min(1).optional(),
    status: z.enum(['ACTIVE', 'INACTIVE']).optional(),
    /*
     * `z.enum` rather than `z.coerce.boolean()` — risk R8. Coercion is
     * truthiness-based, so the STRING "false" arrives truthy and `?isPlatformAdmin=false`
     * did the opposite of what it says. Converted with `=== 'true'` in the
     * service rather than a `.transform`, which would make it a ZodEffects and
     * drop the parameter from the published OpenAPI spec.
     */
    isPlatformAdmin: z.enum(['true', 'false']).optional(),
})
    .strict();
/** Platform view of a user. Never includes credential material. */
export const platformUserSchema = z.object({
    id: z.string().uuid(),
    email: z.string(),
    displayName: z.string(),
    status: z.string(),
    isPlatformAdmin: z.boolean(),
    /** Count only — a platform admin sees reach without a tenant-by-tenant dump. */
    activeMembershipCount: z.number().int(),
    /**
     * Sessions that can still be resumed without signing in again.
     *
     * On the LIST because "is anyone still signed in" is a question asked about a
     * page of users, not about one — and answering it by opening each account in
     * turn is how it stops being asked.
     */
    activeSessionCount: z.number().int(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const platformUserListResponseSchema = paginated(platformUserSchema);
/**
 * Grant or revoke the platform capability.
 *
 * Deliberately its own request, not a field on the user update: granting
 * platform authority is the highest-privilege action in the system, and it
 * should not be possible to change it as a side effect of editing a display
 * name. `.strict()` and the absence of any `isPlatformAdmin` field mean the
 * intent is carried by the ROUTE, never by a body value a client controls.
 */
export const platformAdminChangeRequestSchema = z
    .object({ note: z.string().trim().min(1).max(500).optional() })
    .strict();
/** Creation returns the one-time temporary password; reads never do. */
export const platformUserCreatedSchema = platformUserSchema.extend({
    /**
     * ⚠️ NULLABLE now, because an invited account has no password to show — it carries an
     * UNUSABLE hash until somebody holding the link chooses one. Exactly one of this and
     * `invitation` is ever populated, and a reader must be able to tell which happened: a
     * blank where a one-time secret should be is indistinguishable from a secret that failed
     * to generate.
     */
    temporaryPassword: z.string().nullable(),
    /** Present when `invite` was asked for. The shared shape, never a second one. */
    invitation: invitationIssuedSchema.nullable().default(null),
});
// ── platform identity echo ───────────────────────────────────────────────────
/**
 * Confirms the caller holds a live platform capability. Deliberately returns
 * the database-verified values, not the JWT claims, so it doubles as the
 * smoke test that PlatformAdminGuard re-reads state.
 */
export const platformMeResponseSchema = z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    displayName: z.string(),
    isPlatformAdmin: z.literal(true),
    status: z.string(),
});
// ── login sessions ──────────────────────────────────────────────────────────
/**
 * One login, and what is known about it.
 *
 * A session is a token FAMILY: the chain of rotations descending from a single
 * sign-in. Platform-scoped deliberately — `User` is global (ADR 0009), so a
 * session belongs to an identity rather than to a tenant, and showing one to a
 * tenant administrator would show them a person's logins across organisations
 * they have nothing to do with.
 */
export const loginSessionSchema = z.object({
    /** The token family. Stable for the life of the session. */
    id: z.string().uuid(),
    startedAt: z.string().datetime(),
    /**
     * When the session last renewed its access token.
     *
     * NOT "last request": an access token carries no session identifier, so a
     * request cannot be attributed to a family without a write on every call.
     * Access tokens live an hour, so a renewal inside the last hour means the
     * session is genuinely in use — and the wording says renewed rather than seen,
     * because a reader would take the latter as presence.
     */
    lastRenewedAt: z.string().datetime(),
    /** When the session dies on its own, with no action taken. */
    expiresAt: z.string().datetime(),
    userAgent: z.string().nullable(),
    ipAddress: z.string().nullable(),
    /**
     * The address it STARTED from, when that differs from the current one.
     *
     * Null when unchanged, so a populated value always means something moved.
     * Evidence, not a verdict: a phone leaving wifi does this, and so does a
     * stolen token.
     */
    originalIpAddress: z.string().nullable(),
    /** Access-token renewals since sign-in. A rough measure of use. */
    renewals: z.number().int(),
    /**
     * Whether it can still be resumed WITHOUT signing in again.
     *
     * Deliberately not called "online". Nothing here knows whether somebody is at
     * a keyboard, and a list that implied it would be believed.
     */
    active: z.boolean(),
    /** LOGOUT | ROTATED | REUSE_DETECTED | SUPERSEDED | REVOKED_BY_ADMIN. */
    endedReason: z.string().nullable(),
});
export const loginSessionsResponseSchema = z.object({
    items: z.array(loginSessionSchema),
    /**
     * How many can still be resumed. Stated by the server so a client is not
     * recomputing a judgement the server already made.
     */
    activeCount: z.number().int(),
});
export const revokeSessionsResponseSchema = z.object({
    /** Token rows revoked. Zero is a real answer, not a failure. */
    revoked: z.number().int(),
    /**
     * True when every session was ended by moving the user's token version, which
     * also invalidates access tokens already issued.
     */
    allSessions: z.boolean(),
});
/**
 * Everyone with a live session, across every account.
 *
 * The per-user list answers "is this person still signed in". This answers "who
 * is signed in", which is the question asked during an incident and cannot be
 * assembled by opening accounts one at a time.
 */
export const activeSessionSchema = loginSessionSchema.extend({
    userId: z.string().uuid(),
    userEmail: z.string(),
    userDisplayName: z.string(),
    /** So a platform administrator's session is recognisable at a glance. */
    isPlatformAdmin: z.boolean(),
});
export const activeSessionsResponseSchema = z.object({
    items: z.array(activeSessionSchema),
    /**
     * Distinct people, not sessions — one person with four devices is one person,
     * and a count of sessions would overstate how many are affected by anything.
     */
    distinctUsers: z.number().int(),
    /**
     * True when the list was capped. Stated so a reader does not take a truncated
     * page as the whole picture during exactly the moment it matters.
     */
    truncated: z.boolean(),
});
/** Rows returned by the all-sessions view before it says it truncated. */
export const ACTIVE_SESSION_LIMIT = 200;
//# sourceMappingURL=platform.js.map